import { useCallback, useEffect, useMemo, useState } from 'react'
import { Info } from 'lucide-react'
import {
  StrategyOptimizerView,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components'
import type {
  OptimizerParamDisplay,
  OptimizerParamGroups,
  OptimizerRow,
  StrategyOptimizerViewState,
} from '@/components'
import { botApi, OptimizerResult } from '../api'

interface BotOptimizerProps {
  bid: string
  refreshTrigger?: number
}

const RANGES_SKIP_KEYS = new Set(['mode', 'optimize_for'])
const DEFAULT_RANGES_GROUP = 'Parameters'
const DEFAULT_WINNER_GROUP = 'Parameters'

function snakeToTitle(input: string | undefined | null): string {
  if (!input) return ''
  return input
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase())
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') return String(value)
  if (Array.isArray(value)) {
    return value.length === 0 ? '—' : `[${value.join(', ')}]`
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>
    if (Array.isArray(obj.values)) return `[${(obj.values as unknown[]).join(', ')}]`
    return JSON.stringify(obj)
  }
  return String(value)
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value)
  )
}

function buildRanges(
  optimizationConfig: Record<string, unknown> | undefined,
): OptimizerParamGroups {
  if (!optimizationConfig) return {}
  const groups: OptimizerParamGroups = {}
  for (const [key, value] of Object.entries(optimizationConfig)) {
    if (RANGES_SKIP_KEYS.has(key)) continue
    if (isPlainObject(value) && !('values' in value)) {
      const entries: OptimizerParamDisplay[] = Object.entries(value).map(
        ([k, v]) => ({ label: snakeToTitle(k), value: renderValue(v) }),
      )
      if (entries.length > 0) groups[snakeToTitle(key)] = entries
    } else {
      const bucket = groups[DEFAULT_RANGES_GROUP] ?? []
      bucket.push({ label: snakeToTitle(key), value: renderValue(value) })
      groups[DEFAULT_RANGES_GROUP] = bucket
    }
  }
  return groups
}

function buildWinnerParams(
  params: Record<string, unknown> | undefined,
): OptimizerParamGroups {
  if (!params) return {}
  const entries: OptimizerParamDisplay[] = Object.entries(params).map(
    ([k, v]) => ({ label: snakeToTitle(k), value: renderValue(v) }),
  )
  if (entries.length === 0) return {}
  return { [DEFAULT_WINNER_GROUP]: entries }
}

type OptimizerWinners = NonNullable<
  NonNullable<OptimizerResult['resultSummary']>['data']
>['winners']

function buildRows(winners: OptimizerWinners | undefined): OptimizerRow[] {
  if (!Array.isArray(winners)) return []
  return winners.map((w) => {
    const metrics = w.metrics ?? {}
    return {
      rank: w.rank,
      returnPct: metrics.return_percentage ?? 0,
      sharpe: metrics.sharpe_ratio ?? 0,
      profitFactor: metrics.profit_factor ?? 0,
      maxDD: metrics.max_drawdown ?? 0,
      winRate: metrics.win_rate ?? 0,
      trades: metrics.total_trades ?? 0,
      finalBalance: Math.round(metrics.final_balance ?? 0),
      params: buildWinnerParams(w.params),
    }
  })
}

const ToolbarInfoSlot = (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          onClick={(e) => e.stopPropagation()}
          className="cursor-help text-muted-foreground hover:text-foreground transition-colors"
        >
          <Info size={13} />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[260px]">
        The optimizer tests thousands of parameter combinations through
        automated backtesting, then surfaces the top 10 results ranked by your
        chosen metric.
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

export default function BotOptimizer({ bid, refreshTrigger = 0 }: BotOptimizerProps) {
  const [optimizer, setOptimizer] = useState<OptimizerResult | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await botApi.getLatestOptimizer(bid)
      setOptimizer(data)
    } catch (err) {
      console.error('Error loading optimizer:', err)
      setError(
        (err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: unknown }).message ?? '')
          : '') || 'Failed to load optimizer results.',
      )
    } finally {
      setIsLoading(false)
    }
  }, [bid])

  useEffect(() => {
    load()
  }, [load, refreshTrigger])

  const rows = useMemo(
    () => buildRows(optimizer?.resultSummary?.data?.winners),
    [optimizer],
  )

  const ranges = useMemo(
    () => buildRanges(optimizer?.configInput?.optimization_config),
    [optimizer],
  )

  const focusLabel = useMemo(() => {
    const raw = optimizer?.resultSummary?.data?.stats?.optimize_for
    return raw ? snakeToTitle(raw) : '—'
  }, [optimizer])

  if (isLoading) {
    return (
      <div className="h-full overflow-y-auto bg-card p-4">
        <div className="rounded-xl border border-border bg-card p-10 flex items-center justify-center text-body text-muted-foreground">
          Loading optimizer results…
        </div>
      </div>
    )
  }

  const apiError = optimizer?.resultSummary?.error
  if (error || apiError) {
    return (
      <div className="h-full overflow-y-auto bg-card p-4">
        <div className="rounded-xl border border-danger bg-danger-dim p-4 text-body text-danger">
          {error || apiError}
        </div>
      </div>
    )
  }

  let state: StrategyOptimizerViewState = 'loaded'
  if (!optimizer) {
    state = 'empty'
  } else if (optimizer.status === 0 || optimizer.status === 1) {
    state = 'running'
  }

  return (
    <StrategyOptimizerView
      state={state}
      rows={rows}
      ranges={ranges}
      focusLabel={focusLabel}
      toolbarInfoSlot={ToolbarInfoSlot}
      emptyTitle="No optimization results yet"
      emptyBody="Run an optimizer from chat to find the best parameter combinations for your strategy."
    />
  )
}
